import React, { useState, useEffect, useContext } from "react";
import { useNavigate, NavLink } from "react-router-dom";

import { useMutation, useQuery } from "@apollo/client";
import toast from "react-hot-toast";

import Filter from "./components/ClaimsFilter";
import claimsGQL from "../../gql/claims";
import gqlPreAuth from "../../gql/preAuth";
import { appContext } from "../../context/ContextProvider";
import Pagination from "../../components/Pagination";
import "./styles/pre-auth.css";
import "../Beneficiary/styles/BeneficiaryList.css";
import AppErrorAlert from "../../components/AppErrorAlert";
import ClaimJsonViewModal from "./ClaimJsonViewModal";
import { Alert, Badge, Chip, Tooltip } from "@mui/material";
import { MdArrowCircleUp, MdOutlineRemoveRedEye } from "react-icons/md";
import { IoMdCloseCircleOutline } from "react-icons/io";
import { FaArrowUpFromBracket, FaRegCircleCheck } from "react-icons/fa6";
import { TbEditCircle } from "react-icons/tb";
import { BsFiletypeJson } from "react-icons/bs";
import ClaimReportModal from "./components/ClaimReportModal";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

import { styled, alpha } from "@mui/material/styles";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { FileUpload, HistoryOutlined } from "@mui/icons-material";
import UploadClaimModal from "./UploadClaimModal";
import { RiMessage2Line } from "react-icons/ri";
import ConformationModal from "../../components/ConformationModal";
import { useTranslation } from "react-i18next";
import ClaimUploadListSkeleton from "../../common/ClaimUploadListSkeleton";
import TableSkeletonLoader from "../../common/TableLoading";
import AppButton from "../../shared/AppButton";
import { FaFilter } from "react-icons/fa";
import AppTable from "../../shared/AppTable";
import AppModal from "../../components/AppModal";
import PollIcon from "@mui/icons-material/Poll";

function ClaimsList(props) {
  const { t, i18n } = useTranslation();
  const { setShowLoader, setShowErrorAlert } = useContext(appContext);
  const navigate = useNavigate();
  const currentDate = new Date();
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(() => {
    const saved = sessionStorage.getItem("claimsShowFilters");
    return saved !== null ? JSON.parse(saved) : true;
  });

  useEffect(() => {
    sessionStorage.setItem("claimsShowFilters", JSON.stringify(showFilters));
  }, [showFilters]);

  const [excelData, setExcelData] = useState([]);
  const [errorData, setErrorData] = useState([]);
  const [allClaims, setAllClaims] = useState([]);
  const [claimsTotal, setClaimsTotal] = useState(0);
  const [claimsPages, setClaimsPages] = useState(0);

  const [reportOpen, setReportOpen] = useState(false);
  const handleReportOpen = () => setReportOpen(true);
  const handleReportClose = () => setReportOpen(false);

  const claimsFilterDate = removeEmptyKeys(
    JSON.parse(localStorage.getItem("claimsFilterDate")),
  );

  const [activeTab, setActiveTab] = useState(() => {
    const saved = sessionStorage.getItem("claimsActiveTab");
    return saved !== null ? JSON.parse(saved) : "";
  });

  useEffect(() => {
    sessionStorage.setItem("claimsActiveTab", JSON.stringify(activeTab));
  }, [activeTab]);

  const [item, setItem] = useState({});
  const [showFilter, setShowFilter] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showUploadClaimModal, setShowUploadClaimModal] = useState(false);

  // Stats and Error List States
  const [stats, setStats] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const fromDate = `${currentDate.getFullYear()}-${String(
    currentDate.getMonth() + 1,
  ).padStart(2, "0")}-01`;

  const toDate = `${currentDate.getFullYear()}-${String(
    currentDate.getMonth() + 1,
  ).padStart(2, "0")}-${new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1,
    0,
  ).getDate()}`;

  const [limit, setLimit] = useState(15);
  const [pageNo, setPageNo] = useState(1);

  const [claimsFilters, setClaimsFilters] = useState(() => {
    const saved = sessionStorage.getItem("claimsFiltersSaved");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error("Failed to parse saved claims filters", e);
      }
    }
    return {
      from_date: fromDate,
      to_date: toDate,
      limit: 15,
      page: 1,
      payer_id: "",
      type: "",
      patient_id: "",
      batch_id: "",
      tp_ref_no: "",
      ...(activeTab && { outcome: activeTab }),
      from_date_ordered: "",
      to_date_ordered: "",
      status: "",
      request_bundle_id: "",
      is_followup_req: "",
    };
  });

  useEffect(() => {
    sessionStorage.setItem("claimsFiltersSaved", JSON.stringify(claimsFilters));
  }, [claimsFilters]);

  const {
    loading: dataLoading,
    error,
    data,
    refetch,
  } = useQuery(claimsGQL.GET_CLAIMS_BY_FILTER, {
    variables: {
      input: { ...removeEmptyKeys(claimsFilters) },
    },
  });

  useEffect(() => {
    if (data?.listClaimTransactions) {
      setAllClaims(data?.listClaimTransactions?.data || []);
      setClaimsPages(data?.listClaimTransactions?.pages || 0);
      setClaimsTotal(data?.listClaimTransactions?.total || 0);
      setStats(data?.listClaimTransactions?.stats || null);
    } else {
      setAllClaims([]);
      setStats(null);
    }
  }, [data]);

  const handleStatErrorClick = (errorType, errorItem) => {
    const typeMapping = {
      error: "error",
      abnormal: "Abnormal Request",
      exception_error: "Exception Error",
      validation_error: "Validation Error",
    };

    const input = {
      from_date: claimsFilters.from_date,
      to_date: claimsFilters.to_date,
      outcome: typeMapping[errorType] || errorType,
      error_value: errorItem.error_code,
      error: errorItem.error_message,
      limit: 50,
      page: 1,
    };

    navigate("/claims/error-list", {
      state: {
        filterInput: input,
        errorDetails: errorItem,
      },
    });
  };

  useEffect(() => {
    if (error?.graphQLErrors[0]?.message) {
      toast.error(error?.graphQLErrors[0]?.message);
    }
  }, [error]);

  const [open, setOpen] = useState(false);
  const [selectedItem, setSeelectedItem] = useState(null);

  const handleClickOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const formatDateTime = (time) => {
    if (!time) return "";
    const sanitizedTime = time.replace(/\s\+\d{4}$/, "");
    const dateTime = new Date(sanitizedTime);
    if (isNaN(dateTime)) return "Invalid date";
    const dateOnly = dateTime.toLocaleDateString("en-GB");
    const timeOnly = dateTime.toLocaleTimeString(navigator.language, {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${dateOnly} ${timeOnly}`;
  };

  function removeEmptyKeys(obj) {
    const newObj = {};
    for (const key in obj) {
      if (obj[key] !== "") {
        newObj[key] = obj[key];
      }
    }
    return newObj;
  }

  function FormatDate(date) {
    const options = { year: "numeric", month: "numeric", day: "numeric" };
    return new Intl.DateTimeFormat("en-GB", options).format(new Date(date));
  }

  const onDataRowClicked = (event, item) => {
    navigate("/claims/details", { state: { currentItem: item } });
  };

  const onPageChange = async (currentPage) => {
    const filteredClaimsFilterDate = removeEmptyKeys(claimsFilters);
    let filterInput = {
      ...filteredClaimsFilterDate,
      outcome: activeTab,
      page: currentPage,
    };
    try {
      setLoading(true);
      await refetch({ input: filterInput }).then(({ data }) => {
        setAllClaims(data?.listClaimTransactions?.data);
        setClaimsPages(data?.listClaimTransactions?.pages);
        setClaimsTotal(data?.listClaimTransactions?.total);
      });
    } finally {
      setLoading(false);
    }
    setClaimsFilters((prev) => ({
      ...prev,
      outcome: activeTab,
      page: currentPage,
    }));
  };

  // Check Status
  const [
    checkReq,
    { data: checkData, loading: checkLoading, error: checkError },
  ] = useMutation(claimsGQL.CHECK_STATUS, {
    update(proxy, result) {
      onSuccessfulCheckRequestResponse(result.data);
    },
    onError(error) {
      onCancelResponseError(error.graphQLErrors[0]);
    },
  });

  const onSuccessfulCheckRequestResponse = (data) => {
    if (!data.checkStatusRequest.nphies_err) {
      toast.success("Successfully Requested!");
    } else if (data.checkStatusRequest.nphies_err) {
      setErrorData(data.checkStatusRequest.nphies_err);
      setShowErrorAlert(true);
    } else {
      toast.error(`Unknown Error`);
    }
  };

  const checkClicked = (item) => {
    toast.loading("Requesting!");
    checkReq({
      variables: {
        input: { transaction_id: item.transaction_id, request_name: "claims" },
      },
    });
  };

  // Cancel Request
  const [
    cancelReq,
    { data: cancelData, loading: cancelLoading, error: cancelError },
  ] = useMutation(gqlPreAuth.CANCEL_REQUEST, {
    update(proxy, result) {
      onSuccessfulCancelRequestResponse(result.data);
    },
    onError(error) {
      onCancelResponseError(error.graphQLErrors[0]);
    },
  });

  const onSuccessfulCancelRequestResponse = (data) => {
    if (data.cancelRequest) {
      toast.success("Successfully Cancelled!");
    }
  };

  const onCancelResponseError = (error) => {
    toast.error(`${error.message}`);
  };

  const cancelClicked = async () => {
    cancelReq({
      variables: {
        input: {
          transaction_id: selectedItem?.transaction_id,
          request_name: "claims",
        },
      },
    }).then(async (res) => {
      const claimsFilterDate = JSON.parse(
        localStorage.getItem("claimsFilterDate"),
      );
      let filterInput = {
        ...claimsFilterDate,
        page: pageNo,
        limit: limit,
        outcome: activeTab,
      };
      const filteredClaimsFilterDate = removeEmptyKeys(filterInput);
      try {
        setLoading(true);
        await refetch({ input: filteredClaimsFilterDate }).then(({ data }) => {
          setAllClaims(data?.listClaimTransactions?.data);
          setClaimsPages(data?.listClaimTransactions?.pages);
          setClaimsTotal(data?.listClaimTransactions?.total);
        });
      } finally {
        setLoading(false);
      }
    });
    setSeelectedItem(null);
    setShowConfirmModal(false);
  };

  const {
    loading: pollLoading,
    error: pollError,
    data: pollData,
    refetch: refetchPoll,
  } = useQuery(claimsGQL.CLAIMS_POLL_REQ, { skip: true });

  const handleTabClick = async (tabName) => {
    setActiveTab(tabName);
    let filterInput = { ...claimsFilters, outcome: tabName };
    const filteredClaimsFilterDate = removeEmptyKeys(filterInput);
    try {
      setLoading(true);
      await refetch({ input: filteredClaimsFilterDate }).then(({ data }) => {
        setAllClaims(data?.listClaimTransactions?.data);
        setClaimsPages(data?.listClaimTransactions?.pages);
        setClaimsTotal(data?.listClaimTransactions?.total);
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusStyles = (status) => {
    const s = status?.toLowerCase();
    if (s === "approved" || s === "complete" || s === "success") {
      return { bg: "#dcfce7", text: "#15803d" };
    } else if (
      s === "rejected" ||
      s === "error" ||
      s === "abnormal request" ||
      s === "exception error" ||
      s === "validation error"
    ) {
      return { bg: "#fee2e2", text: "#b91c1c" };
    } else if (s === "pending" || s === "queued") {
      return { bg: "#fef9c3", text: "#854d0e" };
    } else if (s === "pended" || s === "not-required") {
      return { bg: "#dbeafe", text: "#1d4ed8" };
    } else {
      return { bg: "#f1f5f9", text: "#475569" };
    }
  };

  const StyledMenu = styled((props) => (
    <Menu
      elevation={0}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      {...props}
    />
  ))(({ theme }) => ({
    "& .MuiPaper-root": {
      borderRadius: 6,
      marginTop: theme.spacing(1),
      minWidth: 180,
      color:
        theme.palette.mode === "light"
          ? "rgb(55, 65, 81)"
          : theme.palette.grey[300],
      boxShadow:
        "rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
      "& .MuiMenu-list": { padding: "4px 0" },
      "& .MuiMenuItem-root": {
        "& .MuiSvgIcon-root": {
          fontSize: 18,
          color: theme.palette.text.secondary,
          marginRight: theme.spacing(1.5),
        },
        "&:active": {
          backgroundColor: alpha(
            theme.palette.primary.main,
            theme.palette.action.selectedOpacity,
          ),
        },
      },
    },
  }));

  const [anchorEl, setAnchorEl] = React.useState(null);
  const openMenu = Boolean(anchorEl);

  const tabs = [
    { label: t("All Claims"), value: "" },
    { label: t("Queued"), value: "queued" },
    { label: t("Error"), value: "error" },
    { label: t("Abnormal Request"), value: "Abnormal Request" },
    { label: t("Exception Error"), value: "Exception Error" },
    { label: t("Validation Error"), value: "Validation Error" },
    { label: t("Complete"), value: "complete" },
  ];

  const columns = [
    {
      header: t("SQ #"),
      render: (row, index) =>
        claimsFilters?.page * claimsFilters?.limit +
        index +
        1 -
        claimsFilters?.limit,
    },
    {
      header: t("Status"),
      render: (row) => {
        const styles = getStatusStyles(row?.status || "default");
        return (
          <div className="flex flex-col gap-1">
            {row?.status && (
              <Chip
                label={row.status}
                size="small"
                sx={{
                  backgroundColor: styles.bg,
                  color: styles.text,
                  fontWeight: "700",
                  fontSize: "10px",
                  borderRadius: "6px",
                  textTransform: "uppercase",
                  height: 20,
                }}
              />
            )}
            {!row?.is_valid && (
              <span className="bg-red-50 text-red-600 text-center text-[9px] px-1 rounded border border-red-200 font-bold uppercase py-0.5">
                {t("INVALID")}
              </span>
            )}
          </div>
        );
      },
    },
    {
      header: t("Outcome"),
      render: (row) => {
        if (!row?.outcome) return "---";
        const styles = getStatusStyles(row.outcome);
        return (
          <div className="flex flex-col gap-1">
            <Chip
              label={row.outcome}
              size="small"
              sx={{
                backgroundColor: styles.bg,
                color: styles.text,
                fontWeight: "700",
                fontSize: "10px",
                borderRadius: "6px",
                textTransform: "uppercase",
                height: 20,
              }}
            />
          </div>
        );
      },
    },
    {
      header: t("Errors/Issues"),
      render: (row) => {
        const hasErrors = row.errors && row.errors.length > 0;
        const hasDisposition = !!row.disposition;
        if (!hasErrors && !hasDisposition) return "---";
        return (
          <Tooltip
            arrow
            title={
              <div className="p-2 max-w-xs">
                {hasDisposition && (
                  <div className="mb-2">
                    <span className="font-bold text-yellow-400">
                      Disposition:
                    </span>
                    <p className="text-xs">{row.disposition}</p>
                  </div>
                )}
                {hasErrors && (
                  <div>
                    <span className="font-bold text-red-400">
                      Technical Errors:
                    </span>
                    {row.errors.map((err, i) => (
                      <p
                        key={i}
                        className="text-[11px] border-b border-gray-600 py-1 last:border-0"
                      >
                        {err.error_code}: {err.error_message}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            }
          >
            <div className="flex items-center gap-1 text-red-500 cursor-help">
              <span className="text-[10px] font-bold underline decoration-dotted uppercase">
                {t("View Details")}
              </span>
            </div>
          </Tooltip>
        );
      },
    },
    // --- Updated Status Column ---
    {
      header: t("Duplicate"),
      render: (row) =>
        row?.is_duplicate ? (
          <Tooltip title={`${t("Original Ref")}: ${row.duplicate_ref}`}>
            <Chip
              label={t("Duplicate")}
              size="small"
              sx={{
                backgroundColor: "#fee2e2",
                color: "#b91c1c",
                fontWeight: "700",
                fontSize: "10px",
                borderRadius: "6px",
                height: 20,
              }}
            />
          </Tooltip>
        ) : (
          "---"
        ),
    },
    {
      header: t("Beneficiary"),
      render: (row) => (
        <div className="capitalize font-medium text-gray-700 w-[220px] block whitespace-pre-wrap">
          {row?.patient_name}
        </div>
      ),
    },
    {
      header: t("Payer"),
      render: (row) => (
        <span className="text-gray-700 w-[240px] block whitespace-pre-wrap">{row?.payer_name}</span>
      ),
    },
    { header: t("Batch ID"), field: "batch_id" },
    {
      header: t("Date Ordered"),
      render: (row) => FormatDate(row?.date_ordered),
    },
    { header: t("Type"), field: "type" },
    { header: t("Episode No."), field: "episode_no" },
  ];

  return (
    <>
      <div className="pre_auth_container !p-0">
        {/* Header Buttons */}
        <div className="flex justify-between flex-col sm:flex-row gap-2 mb-2">
          <div className="w-full sm:w-[calc(100%-150px)]">
            <span className="font-semibold text-black/70 text-lg ">
              {t("CLAIMS TRANSACTION")}
              <span className=" text-lg ml-1">
                ({data?.listClaimTransactions?.total || 0}{" "}
                <span className="text-xs font-bold italic">{t("Entries")}</span>
                )
              </span>
            </span>
          </div>
          <div className="flex w-full gap-1.5 justify-end flex-col sm:flex-row">
            {stats && (
              <AppButton
                label={t("Show Errors")}
                variant="outlined"
                onClick={() => setShowErrorModal(true)}
                startIcon={<PollIcon />}
                className="!border-primary !text-primary hover:!bg-primary/10"
              />
            )}
            <AppButton
              label={t("Request Claim")}
              onClick={() => navigate("/claims/form")}
              startIcon={<AddIcon />}
            />
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="bg-white border border-slate-200 rounded-lg p-2 mb-2">
            <Filter
              refetch={refetch}
              setLoading={setLoading}
              activeTab={activeTab}
              setAllClaims={setAllClaims}
              setClaimsTotal={setClaimsTotal}
              setClaimsPages={setClaimsPages}
              claimsFilters={claimsFilters}
              setClaimsFilters={setClaimsFilters}
              fromDate={fromDate}
              toDate={toDate}
            />
          </div>
        )}

        <AppModal
          open={showErrorModal}
          onClose={() => setShowErrorModal(false)}
          title={t("Claims Error Breakdown")}
          maxWidth="lg"
          contentSx={{ overflow: "hidden" }}
        >
          {(() => {
            const validationErrors = stats?.validation_error || [];
            const isFewValidationErrors = validationErrors.length < 4;
            const leftColSpan = isFewValidationErrors
              ? "md:col-span-2"
              : "md:col-span-3";
            const rightColSpan = isFewValidationErrors
              ? "md:col-span-2"
              : "md:col-span-1";

            return (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                {/* Left Column: Validation Errors (Big) */}
                <div className={leftColSpan}>
                  {[
                    {
                      label: t("Validation Errors"),
                      key: "validation_error",
                      color: "bg-amber-50 text-amber-600 border-amber-100",
                    },
                  ].map((statInfo) => {
                    const errorList = stats?.[statInfo.key] || [];
                    const totalCount = errorList.reduce(
                      (acc, curr) => acc + curr.count,
                      0,
                    );
                    return (
                      <div
                        key={statInfo.key}
                        className={`border rounded-lg p-2 flex flex-col h-full ${statInfo.color}`}
                      >
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-xs font-black uppercase tracking-wider">
                            {statInfo.label}
                          </span>
                          <span className="text-sm font-black">
                            {totalCount}
                          </span>
                        </div>
                        <div className="flex-grow max-h-[350px] overflow-y-auto space-y-2 custom-scrollbar pr-1">
                          {errorList.length > 0 ? (
                            errorList.map((err, idx) => (
                              <div
                                key={idx}
                                className="text-[11px] bg-white/70 p-1 rounded-lg border border-current/10 cursor-pointer hover:bg-white transition-all duration-200"
                                onClick={() =>
                                  handleStatErrorClick(statInfo.key, err)
                                }
                              >
                                <div className="flex justify-between items-center gap-4">
                                  <span className="font-bold flex-shrink-0 bg-current/10 px-2 py-1 rounded">
                                    {err.error_code}
                                  </span>
                                  <span className="flex-grow font-medium leading-tight">
                                    {err.error_message}
                                  </span>
                                  <span className="font-black bg-current/20 px-2 py-0.5 rounded-full text-[11px]">
                                    {err.count}
                                  </span>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="text-sm opacity-60 text-center py-10">
                              {t("No validation issues found")}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Right Column: Other 3 Stats stacked */}
                <div className={`${rightColSpan} flex flex-col gap-2`}>
                  {[
                    {
                      label: t("General Errors"),
                      key: "error",
                      color: "bg-red-50 text-red-600 border-red-100",
                    },
                    {
                      label: t("Abnormal Requests"),
                      key: "abnormal",
                      color: "bg-orange-50 text-orange-600 border-orange-100",
                    },
                    {
                      label: t("Exception Errors"),
                      key: "exception_error",
                      color: "bg-purple-50 text-purple-600 border-purple-100",
                    },
                  ].map((statInfo) => {
                    const errorList = stats?.[statInfo.key] || [];
                    const totalCount = errorList.reduce(
                      (acc, curr) => acc + curr.count,
                      0,
                    );
                    return (
                      <div
                        key={statInfo.key}
                        className={`border rounded-lg p-2 flex flex-col flex-1 ${statInfo.color}`}
                      >
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-[11px] font-bold uppercase truncate">
                            {statInfo.label}
                          </span>
                          <span className="text-sm font-black">
                            {totalCount}
                          </span>
                        </div>
                        <div className="max-h-[100px] overflow-y-auto space-y-1.5 custom-scrollbar pr-1">
                          {errorList.length > 0 ? (
                            errorList.map((err, idx) => (
                              <div
                                key={idx}
                                className="text-[11px] bg-white/50 p-1.5 rounded border border-current/5 cursor-pointer hover:bg-white transition-colors shadow-sm"
                                onClick={() =>
                                  handleStatErrorClick(statInfo.key, err)
                                }
                              >
                                <div className="flex justify-between items-start gap-1">
                                  <span className="font-bold whitespace-nowrap opacity-90">
                                    {err.error_code}:
                                  </span>
                                  <span className="truncate opacity-80">
                                    {err.error_message}
                                  </span>
                                  <span className="font-black ml-auto">
                                    {err.count}
                                  </span>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="text-[10px] opacity-60 text-center py-1">
                              {t("No issues")}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </AppModal>

        {/* Tabs */}
        <div className="my-2">
          <ul className="flex flex-wrap text-center text-gray-500 bg-gray-100 rounded-lg p-1 shadow-inner gap-0.5">
            {tabs.map((tab) => (
              <li key={tab.value} className="flex-1">
                <a
                  className={`flex justify-center py-0.5 cursor-pointer text-[13px] rounded-lg transition-colors hover:text-[#0070C2] ${
                    activeTab === tab.value ? "bg-[#0070C2] !text-white" : ""
                  }`}
                  onClick={() => handleTabClick(tab.value)}
                >
                  {tab.label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Table */}
        <div>
          <AppTable
            columns={columns}
            data={allClaims}
            loading={loading || dataLoading}
            bodyRowPadding="7px"
            onView={(row) => onDataRowClicked(null, row)}
            onEdit={(row) =>
              navigate("/claims/edit/form/" + row.transaction_id)
            }
            onJsonView={(row) => {
              setSeelectedItem(row);
              setOpen(true);
            }}
            onCheck={(row) => checkClicked(row)}
            onCancel={(row) => {
              setSeelectedItem(row);
              setShowConfirmModal(true);
            }}
            onCommunication={(row) => onDataRowClicked(null, row)}
            onFollowUp={(row) =>
              navigate(`/claims/edit/form/${row?.transaction_id}/follow-up`)
            }
            LoaderComponent={
              <TableSkeletonLoader
                columnCount={columns.length + 1}
                rowCount={10}
              />
            }
          />
        </div>

        {/* Pagination */}
        <div>
          <Pagination
            currentPage={claimsFilters?.page}
            totalPages={claimsPages}
            onPageChange={onPageChange}
          />
        </div>
      </div>

      <AppErrorAlert data={errorData} />

      <ConformationModal
        open={showConfirmModal}
        setOpen={setShowConfirmModal}
        onDelete={() => cancelClicked()}
        text={"Cancel"}
      />
      <ClaimJsonViewModal
        setOpen={setOpen}
        open={open}
        handleClose={handleClose}
        selectedItem={selectedItem}
      />
      <ClaimReportModal
        open={reportOpen}
        setOpen={setReportOpen}
        handleClose={handleReportClose}
        handleClickOpen={handleReportOpen}
      />
      <UploadClaimModal
        showUploadClaimModal={showUploadClaimModal}
        setShowUploadClaimModal={setShowUploadClaimModal}
      />
    </>
  );
}

export default ClaimsList;
